import { Component, OnDestroy, OnInit } from '@angular/core';
import { StateService, TransitionService } from '@uirouter/angular';

import { GfTranslateService } from '../../services/core.translate.service';
import { UserService } from '../../services/core.user.service';
import { SiteHelpService } from './site-help.service';
import { SiteHelpIndexEntry } from './site-help.types';

/** One visible line of the topics tree. */
export interface SiteHelpTopicRow {
    entry: SiteHelpIndexEntry;
    depth: number;
    hasChildren: boolean;
    expanded: boolean;
}

type SiteHelpMode = 'home' | 'page' | 'search';

/**
 * Layout shared by every site-help view: heading, breadcrumbs, and the topics
 * tree with its title filter on the left; the routed view (landing, page or
 * search) in the content panel on the right.
 */
@Component({
    selector: 'gf-site-help',
    template: require('./site-help.html')
})
export class SiteHelpComponent implements OnInit, OnDestroy {
    hasUser = false;
    loading = true;
    tocError = false;

    mode: SiteHelpMode = 'home';
    currentSlug: string | null = null;
    heading = '';
    crumbs: any[] = [];

    /** Small screens only: whether the collapsed topics panel is open. Larger screens always show it. */
    topicsOpen = false;

    findText = '';
    rows: SiteHelpTopicRow[] = [];

    private entries: SiteHelpIndexEntry[] = [];
    private bySlug = new Map<string, SiteHelpIndexEntry>();
    private parentsWithChildren = new Set<string>();
    private expanded = new Set<string>();
    private deregister: (() => void) | null = null;

    constructor(
        private service: SiteHelpService,
        private stateService: StateService,
        private transitionService: TransitionService,
        private userService: UserService,
        private translateService: GfTranslateService
    ) { }

    ngOnInit() {
        this.hasUser = this.userService.session.hasUser();
        this.syncFromState();

        const deregister = this.transitionService.onSuccess({ to: 'home.site-help.**' }, () => {
            // Having chosen a topic (or search), the reader wants the content, not the list.
            this.topicsOpen = false;
            this.syncFromState();
        });
        this.deregister = () => deregister();

        this.service.getIndex()
            .then((index) => {
                this.setEntries(index.pages);
                this.loading = false;
            })
            .catch(() => {
                this.tocError = true;
                this.loading = false;
            });
    }

    ngOnDestroy() {
        if (this.deregister) {
            this.deregister();
        }
    }

    toggleTopics() {
        this.topicsOpen = !this.topicsOpen;
    }

    /** Collapses or expands a branch. Ignored while the title filter is forcing everything open. */
    toggle(slug: string) {
        if (this.expanded.has(slug)) {
            this.expanded.delete(slug);
        } else {
            this.expanded.add(slug);
        }
        this.rebuild();
    }

    /** Empties the title filter and restores the tree. */
    clearFind() {
        this.findText = '';
        this.rebuild();
    }

    /** Re-evaluates the filter, rows and breadcrumbs. Public so the template can call it on input. */
    rebuild() {
        this.rows = this.buildRows();
        this.crumbs = this.buildCrumbs();
        this.heading = this.mode === 'search'
            ? this.translateService.translate('Search')
            : (this.currentSlug && this.bySlug.get(this.currentSlug)?.title) || '';
    }

    private setEntries(entries: SiteHelpIndexEntry[]) {
        this.entries = entries;
        this.bySlug = new Map(entries.map((e) => [e.slug, e]));
        this.parentsWithChildren = new Set(entries.filter((e) => e.parent).map((e) => e.parent as string));
        this.syncFromState();
    }

    /** Reads the routed view and opens the branch that leads to the current page. */
    private syncFromState() {
        const name = this.stateService.current.name;
        if (name === 'home.site-help.page') {
            this.mode = 'page';
            this.currentSlug = this.stateService.params.page || null;
        } else if (name === 'home.site-help.search') {
            this.mode = 'search';
            this.currentSlug = null;
        } else {
            this.mode = 'home';
            this.currentSlug = null;
        }

        if (this.currentSlug) {
            // The page's ancestors, and the page itself when it has children, are shown open.
            this.ancestorsOf(this.currentSlug).forEach((a) => this.expanded.add(a.slug));
            if (this.parentsWithChildren.has(this.currentSlug)) {
                this.expanded.add(this.currentSlug);
            }
        }
        this.rebuild();
    }

    /** Parents of `slug`, nearest first. Guards against a cyclic index. */
    private ancestorsOf(slug: string): SiteHelpIndexEntry[] {
        const result: SiteHelpIndexEntry[] = [];
        const seen = new Set<string>([slug]);
        let parent = this.bySlug.get(slug)?.parent;
        while (parent && !seen.has(parent)) {
            seen.add(parent);
            const entry = this.bySlug.get(parent);
            if (!entry) break;
            result.push(entry);
            parent = entry.parent;
        }
        return result;
    }

    private buildRows(): SiteHelpTopicRow[] {
        const filter = (this.findText || '').trim().toLowerCase();
        let shown: Set<string> | null = null;

        if (filter) {
            // Titles that match, plus their ancestors so each hit is shown in context.
            shown = new Set<string>();
            this.entries
                .filter((e) => e.title.toLowerCase().includes(filter))
                .forEach((e) => {
                    shown!.add(e.slug);
                    this.ancestorsOf(e.slug).forEach((a) => shown!.add(a.slug));
                });
        }

        const depth = new Map<string, number>();
        const visible = new Set<string>();
        const rows: SiteHelpTopicRow[] = [];

        // Entries arrive in display order, so a parent is always seen before its children.
        for (const entry of this.entries) {
            if (shown && !shown.has(entry.slug)) continue;

            let isVisible = true;
            if (entry.parent) {
                // While filtering, branches open on their own; otherwise follow the expanded state.
                isVisible = visible.has(entry.parent) && (shown !== null || this.expanded.has(entry.parent));
            }
            if (!isVisible) continue;

            visible.add(entry.slug);
            depth.set(entry.slug, entry.parent ? (depth.get(entry.parent) || 0) + 1 : 0);
            rows.push({
                entry,
                depth: depth.get(entry.slug) as number,
                hasChildren: this.parentsWithChildren.has(entry.slug),
                expanded: shown !== null || this.expanded.has(entry.slug)
            });
        }
        return rows;
    }

    /** "Site Help" first, then the page's ancestors; the last crumb is the current view and has no link. */
    private buildCrumbs(): any[] {
        const root = this.translateService.translate('Site Help');
        if (this.mode === 'search') {
            return [
                { label: root, state: 'home.site-help.home' },
                { label: this.translateService.translate('Search Help') }
            ];
        }
        const current = this.currentSlug ? this.bySlug.get(this.currentSlug) : null;
        if (!current) {
            return [{ label: root }];
        }
        return [
            { label: root, state: 'home.site-help.home' },
            ...this.ancestorsOf(current.slug).reverse().map((a) => ({
                label: a.title,
                state: 'home.site-help.page',
                params: { page: a.slug }
            })),
            { label: current.title }
        ];
    }
}
